import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import dayjs, { Dayjs } from 'dayjs';
import {
  CourseInstanceStatus,
  CourseInstanceStatusXMode,
  CourseInstanceStatusYMode,
} from '../insightsV2/types/CourseInstanceStatus';
import {
  CourseInstanceLeadTime,
  CourseInstanceLeadTimeXMode,
  CourseInstanceLeadTimeYMode,
} from '../insightsV2/types/CourseInstanceLeadTime';
import Market from '../market/types/Market';
import { SubsidiaryCompanyGoal } from '../subsidiaryCompany/types/SubsidiaryCompany';
import { BucketSizeOption } from '../insightsV2/types/InsightsFilters';

const InsightsContext = React.createContext<{
  market: Market | undefined;
  setMarket: (market: Market | undefined) => void;
  subsidiaryCompanyIds: number[] | undefined;
  setSubsidiaryCompanyIds: (ids: number[] | undefined) => void;
  clientCompanyIds: number[] | undefined;
  setClientCompanyIds: (ids: number[] | undefined) => void;
  instructorIds: number[] | undefined;
  setInstructorIds: (ids: number[] | undefined) => void;
  courseIds: number[] | undefined;
  setCourseIds: (ids: number[] | undefined) => void;
  responsibleBookerIds: number[] | undefined;
  setResponsibleBookerIds: (ids: number[] | undefined) => void;
  invoiceRecipientIds: number[] | undefined;
  setInvoiceRecipientIds: (ids: number[] | undefined) => void;
  startDate: dayjs.Dayjs;
  setStartDate: (startDate: dayjs.Dayjs) => void;
  endDate: dayjs.Dayjs;
  setEndDate: (endDate: dayjs.Dayjs) => void;
  statusXMode: CourseInstanceStatusXMode;
  setStatusXMode: (xMode: CourseInstanceStatusXMode) => void;
  statusYMode: CourseInstanceStatusYMode;
  setStatusYMode: (yMode: CourseInstanceStatusYMode) => void;
  statusBucketSize: number;
  setStatusBucketSize: (statusBucketSize: number) => void;
  selectedStatus: CourseInstanceStatus | null;
  setSelectedStatus: (
    selectedCourseStatus: CourseInstanceStatus | null,
  ) => void;
  leadTimeXMode: CourseInstanceLeadTimeXMode;
  setLeadTimeXMode: (xMode: CourseInstanceLeadTimeXMode) => void;
  leadTimeYMode: CourseInstanceLeadTimeYMode;
  setLeadTimeYMode: (yMode: CourseInstanceLeadTimeYMode) => void;
  leadTimeBucketSize: BucketSizeOption;
  setLeadTimeBucketSize: (leadTimeBucketSize: number) => void;
  selectedLeadTime: CourseInstanceLeadTime | null;
  setSelectedLeadTime: (
    selectedLeadTime: CourseInstanceLeadTime | null,
  ) => void;
  goals: SubsidiaryCompanyGoal[] | undefined;
  setGoals: (goals: SubsidiaryCompanyGoal[] | undefined) => void;
  setDatesAdjustedToBucketSize: (
    startDate: Dayjs,
    endDate: Dayjs,
    bucketSize: BucketSizeOption,
  ) => void;
}>(undefined!); // eslint-disable-line @typescript-eslint/no-non-null-assertion

export const useInsights = () => useContext(InsightsContext);

type InsightsContextProviderProps = PropsWithChildren;

const InsightsContextProvider: React.FC<InsightsContextProviderProps> = ({
  children,
}) => {
  const [market, setMarket] = useState<Market | undefined>(undefined);
  const [subsidiaryCompanyIds, setSubsidiaryCompanyIds] = useState<
    number[] | undefined
  >(undefined);
  const [clientCompanyIds, setClientCompanyIds] = useState<
    number[] | undefined
  >(undefined);
  const [instructorIds, setInstructorIds] = useState<number[] | undefined>(
    undefined,
  );
  const [courseIds, setCourseIds] = useState<number[] | undefined>(undefined);
  const [responsibleBookerIds, setResponsibleBookerIds] = useState<
    number[] | undefined
  >(undefined);
  const [invoiceRecipientIds, setInvoiceRecipientIds] = useState<
    number[] | undefined
  >(undefined);

  // Use sv locale to set mondays as the first day of the week
  const [startDate, setStartDate] = useState<dayjs.Dayjs>(
    dayjs().locale('sv').subtract(29, 'days'),
  );
  const [endDate, setEndDate] = useState<dayjs.Dayjs>(dayjs().locale('sv'));

  const [statusXMode, setStatusXMode] = useState<CourseInstanceStatusXMode>(
    CourseInstanceStatusXMode.BOOKING_CREATED,
  );
  const [statusYMode, setStatusYMode] = useState<CourseInstanceStatusYMode>(
    CourseInstanceStatusYMode.NUMBER_OF_CLASSES,
  );
  const [statusBucketSize, setStatusBucketSize] = useState<number>(
    BucketSizeOption.Daily,
  );
  const [selectedStatus, setSelectedStatus] =
    useState<CourseInstanceStatus | null>(null);

  const [leadTimeXMode, setLeadTimeXMode] =
    useState<CourseInstanceLeadTimeXMode>(
      CourseInstanceLeadTimeXMode.BOOKING_CREATED,
    );
  const [leadTimeYMode, setLeadTimeYMode] =
    useState<CourseInstanceLeadTimeYMode>(
      CourseInstanceLeadTimeYMode.NUMBER_OF_CLASSES,
    );
  const [leadTimeBucketSize, setLeadTimeBucketSize] = useState<number>(
    BucketSizeOption.Daily,
  );
  const [selectedLeadTime, setSelectedLeadTime] =
    useState<CourseInstanceLeadTime | null>(null);

  const [goals, setGoals] = useState<SubsidiaryCompanyGoal[]>();

  const setDatesAdjustedToBucketSize = useCallback(
    (startDate: Dayjs, endDate: Dayjs, bucketSize: BucketSizeOption) => {
      switch (bucketSize) {
        case BucketSizeOption.Daily:
          setStartDate(startDate);
          setEndDate(endDate);
          setStatusBucketSize(BucketSizeOption.Daily);
          break;
        case BucketSizeOption.Weekly:
          setStartDate(startDate.startOf('week'));
          setEndDate(endDate.endOf('week'));
          setStatusBucketSize(BucketSizeOption.Weekly);
          break;
        case BucketSizeOption.Monthly:
          setStartDate(startDate.startOf('month'));
          setEndDate(endDate.endOf('month'));
          setStatusBucketSize(BucketSizeOption.Monthly);
          break;
      }
    },
    [setEndDate, setStartDate],
  );

  useEffect(() => {
    if (market === undefined) {
      setStatusYMode(CourseInstanceStatusYMode.NUMBER_OF_CLASSES);
    }
  }, [market]);

  useEffect(() => {
    if (subsidiaryCompanyIds?.length !== 1) {
      setGoals(undefined);
    }
  }, [subsidiaryCompanyIds]);

  return (
    <InsightsContext.Provider
      value={{
        market,
        setMarket,
        subsidiaryCompanyIds,
        setSubsidiaryCompanyIds,
        clientCompanyIds,
        setClientCompanyIds,
        instructorIds,
        setInstructorIds,
        courseIds,
        setCourseIds,
        responsibleBookerIds,
        setResponsibleBookerIds,
        invoiceRecipientIds,
        setInvoiceRecipientIds,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        statusXMode,
        setStatusXMode,
        statusYMode,
        setStatusYMode,
        statusBucketSize,
        setStatusBucketSize,
        selectedStatus,
        setSelectedStatus,
        leadTimeXMode,
        setLeadTimeXMode,
        leadTimeYMode,
        setLeadTimeYMode,
        leadTimeBucketSize,
        setLeadTimeBucketSize,
        selectedLeadTime,
        setSelectedLeadTime,
        goals,
        setGoals,
        setDatesAdjustedToBucketSize,
      }}>
      {children}
    </InsightsContext.Provider>
  );
};

export default InsightsContextProvider;
